/**
 * @lines98/client/lib/i18n/catalogs — lazy-загрузчик каталогов локализации.
 *
 * ЗАЧЕМ / WHY THIS EXISTS: каталоги 12 языков НЕ должны лететь все сразу в бандл
 * (ADR-0001 bundle <200КБ / CWV; ADR-0021 D-1 «только активный язык»). Единое
 * место, знающее ФАЙЛЫ каталогов: остальной модуль (`./runtime`) работает с
 * абстракцией `loadCatalog(lang)`. Источник каталогов — `content/i18n/<lang>.json`
 * (namespace `game` + chrome-ключи; тот же формат, что держит контент-обвязку).
 *
 * Vite `import.meta.glob(..., { import: 'default', eager: false })` создаёт по
 * dynamic-`import()` на каждый JSON → Vite code-split: в стартовый бандл попадает
 * только тот язык, что реально запрошен (chunk на язык). Кэш загруженных каталогов
 * держим здесь, чтобы повторный `setLocale` того же языка был синхронным.
 *
 * SCOPE: реестр lazy-импортеров каталогов + кэш + `loadCatalog`/`peekCatalog`.
 * NOT IN SCOPE: активный язык + `t()` (./runtime), типы (./types), коды языков
 *   (`@lines98/shared/config/seo`).
 *
 * Cross-references: ADR-0021 D-1; ADR-0001 (bundle-budget, мотив lazy);
 *   `content/i18n/<lang>.json` (источник каталогов).
 */

import type { UiLang } from '@lines98/shared/config/seo';

import type { Catalog } from './types.js';

/**
 * Реестр lazy-импортеров: путь → функция, возвращающая Promise<Catalog>. Vite
 * раскрывает glob на build-time в карту dynamic-import'ов (по chunk на файл).
 * Путь относителен ЭТОМУ файлу: `packages/client/src/lib/i18n` → repo-root
 * `content/i18n` это `../../../../../content/i18n`.
 */
const importers = import.meta.glob<Catalog>('../../../../../content/i18n/*.json', {
  import: 'default',
});

/** Построить ключ реестра для языка (имя файла каталога). */
function globKeyFor(lang: UiLang): string {
  return `../../../../../content/i18n/${lang}.json`;
}

/** Кэш уже загруженных каталогов — повторная загрузка языка синхронна. */
const cache = new Map<UiLang, Catalog>();

/**
 * Загрузить каталог языка (lazy). Резолвится из кэша, если уже грузили. Возвращает
 * null, если для языка нет файла каталога (язык-заглушка без своего JSON) — тогда
 * вызывающий (`./runtime`) опирается на fallback-цепочку (активный → en → ключ).
 */
export async function loadCatalog(lang: UiLang): Promise<Catalog | null> {
  const cached = cache.get(lang);
  if (cached) return cached;
  const importer = importers[globKeyFor(lang)];
  if (!importer) return null; // нет файла каталога для этого языка
  const catalog = await importer();
  cache.set(lang, catalog);
  return catalog;
}

/**
 * Синхронно вернуть уже загруженный каталог языка (или null). Используется `t()`
 * для синхронного резолва: язык должен быть предварительно загружен через
 * `loadCatalog` (это делает `setLocale` в ./runtime до перерисовки).
 */
export function peekCatalog(lang: UiLang): Catalog | null {
  return cache.get(lang) ?? null;
}
